import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';
import { cn } from '@/lib/utils';
import { 
  Wifi, 
  WifiOff, 
  RefreshCw, 
  Clock, 
  CheckCircle,
  AlertTriangle 
} from 'lucide-react';

export const OfflineIndicator = () => {
  const { isOnline, pendingActions, syncPendingActions } = useOfflineStorage();

  if (isOnline && pendingActions === 0) return null;

  return (
    <Card className={cn(
      "fixed top-20 left-4 right-4 z-40 shadow-elegant",
      !isOnline && "border-warning/20 bg-warning/5",
      isOnline && pendingActions > 0 && "border-accent/20 bg-accent/5"
    )}>
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
            !isOnline && "bg-warning/10",
            isOnline && pendingActions > 0 && "bg-accent/10"
          )}>
            {!isOnline ? (
              <WifiOff className="h-4 w-4 text-warning" />
            ) : (
              <Clock className="h-4 w-4 text-accent" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {!isOnline ? 'You\'re Offline' : 'Syncing Changes'}
              </span>
              {pendingActions > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {pendingActions} pending
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {!isOnline 
                ? 'Changes will be saved locally and synced when connection is restored'
                : 'Your offline changes are being synchronized'
              }
            </p>
          </div>
          
          {isOnline && pendingActions > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={syncPendingActions}
              className="h-8 w-8 p-0 flex-shrink-0"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          )}
        </div>
        
        {!isOnline && (
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <CheckCircle className="h-3 w-3 text-success" />
            <span>Offline mode active - your data is safe</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};